import React, { useCallback, useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { enableCoachPush, syncCoachPush } from "../lib/coachPush";

const DISMISS_KEY = "tir-coach-push-banner-dismissed-at";
const DISMISS_MS = 24 * 60 * 60 * 1000;

/**
 * Mounted on every coach page. Silently re-syncs the push subscription on
 * load + whenever the app returns to the foreground, and shows a one-tap
 * banner when this device has never enabled notifications (or blocked them).
 */
export default function CoachPushAutoSync() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - at < DISMISS_MS;
  });

  const sync = useCallback(() => {
    syncCoachPush()
      .then(setState)
      .catch((e) => {
        console.warn("[push] auto-sync failed", e);
        setState("idle");
      });
  }, []);

  useEffect(() => {
    sync();
    const onVis = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [sync]);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await enableCoachPush();
      setState(res);
      if (res === "subscribed") toast.success("Notifications on — you'll be pinged the moment an athlete logs or messages.");
      else if (res === "denied") toast.error("Notifications are blocked for this site in your browser settings.");
    } catch (e) {
      toast.error(e?.message || "Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  if (dismissed || !state || !["idle", "denied", "ios-not-pwa"].includes(state)) return null;

  const copy =
    state === "denied"
      ? "Notifications are blocked on this device — allow them in your browser's site settings to hear from athletes right away."
      : state === "ios-not-pwa"
        ? "On iPhone, add this site to your Home Screen to get instant athlete notifications."
        : "You're not getting push notifications on this device. Turn them on to hear the moment an athlete logs a workout or messages you.";

  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-3 right-3 md:left-auto md:right-6 md:max-w-md z-50 bg-[var(--tir-ink)] text-white px-4 py-3 flex items-center gap-3 text-xs shadow-2xl rounded-sm"
      data-testid="coach-push-banner"
    >
      <Bell size={14} className="shrink-0 text-amber-300" />
      <span className="flex-1 leading-snug">{copy}</span>
      {state === "idle" && (
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          data-testid="coach-push-banner-enable"
          className="shrink-0 px-3 py-1.5 bg-white text-[var(--tir-ink)] text-[10px] tracking-[0.2em] uppercase font-semibold disabled:opacity-60"
        >
          {busy ? "Enabling…" : "Enable"}
        </button>
      )}
      <button type="button" onClick={dismiss} aria-label="Dismiss" data-testid="coach-push-banner-dismiss" className="shrink-0 p-1 text-white/70 hover:text-white">
        <X size={14} />
      </button>
    </div>
  );
}
